import { nanoid } from 'nanoid'
import type {
  BuildingSettings,
  Dimensions2D,
  LegacyPropType,
  Project,
  StandardsTable,
} from './types'
import { SCHEMA_VERSION } from './types'

const IN = 1 / 12 // one inch in feet

/** NBC-India / common South-Indian residential defaults (all editable per project). */
export const DEFAULT_STANDARDS: StandardsTable = {
  exteriorWallThickness: 9 * IN, // 9 in (230 mm)
  interiorWallThickness: 4.5 * IN, // 4.5 in (115 mm)
  floorHeight: 10,
  ceilingHeight: 9.5,
  plinthHeight: 1.5,
  mainDoor: { width: 3.5, depth: 7 },
  internalDoor: { width: 3, depth: 7 },
  bathroomDoor: { width: 2.5, depth: 6.5 },
  window: { width: 4, depth: 4 },
  windowSillHeight: 3,
  stairRiser: 6 * IN,
  stairTread: 11 * IN,
  stairWidth: 3.5,
}

export const DEFAULT_SETTINGS: BuildingSettings = {
  gridSnap: 0.5, // 6 inches
  standards: DEFAULT_STANDARDS,
  style: 'standard',
}

/** Snap increments offered in the toolbar, in feet. */
export const SNAP_INCREMENTS = [1, 0.5, 0.25, 1 / 12] as const // 1ft, 6in, 3in, 1in

/** Sensible defaults seeded when the compound wall is first enabled. */
export const COMPOUND_WALL_DEFAULTS = {
  thickness: 0.5, // 6 in
  height: 4.5,
  inset: 0,
} as const

/** A driveway-width front gate (no standards entry — picked here). */
export const GATE_DEFAULT = { width: 10, height: 5 } as const

/**
 * Default real-world footprint (feet) and height per LEGACY prop type.
 * Used ONLY by migrate.ts to reconstruct params from old stored objects.
 * New code uses PropFamily.dimensions() instead.
 */
export const PROP_DEFAULTS: Record<LegacyPropType, { dim: Dimensions2D; height: number; label: string }> = {
  bed: { dim: { width: 5, depth: 6.5 }, height: 2, label: 'Bed' },
  sofa: { dim: { width: 6, depth: 3 }, height: 2.8, label: 'Sofa' },
  chair: { dim: { width: 1.8, depth: 1.8 }, height: 3, label: 'Chair' },
  table: { dim: { width: 4, depth: 2.5 }, height: 2.5, label: 'Table' },
  toilet: { dim: { width: 1.5, depth: 2.3 }, height: 2.5, label: 'Toilet' },
  sink: { dim: { width: 2, depth: 1.5 }, height: 2.8, label: 'Sink' },
  shower: { dim: { width: 3, depth: 3 }, height: 7, label: 'Shower' },
  kitchenCounter: { dim: { width: 6, depth: 2 }, height: 3, label: 'Counter' },
  tv: { dim: { width: 4, depth: 0.5 }, height: 2.5, label: 'TV' },
  wardrobe: { dim: { width: 4, depth: 2 }, height: 7, label: 'Wardrobe' },
  car: { dim: { width: 6, depth: 14 }, height: 4.8, label: 'Car' },
  bike: { dim: { width: 2.5, depth: 6 }, height: 4, label: 'Bike' },
  door: { dim: { width: 3, depth: 0.5 }, height: 7, label: 'Door' },
  window: { dim: { width: 4, depth: 0.5 }, height: 4, label: 'Window' },
  stairs: { dim: { width: 3.5, depth: 10 }, height: 10, label: 'Stairs' },
}

/** A fresh project: a rectangular plot with default setbacks and one ground level. */
export function createDefaultProject(name = 'Untitled House', plotWidth = 60, plotDepth = 40): Project {
  const now = nowMs()
  const boundary = rect(0, 0, plotWidth, plotDepth)
  const groundId = nanoid()
  return {
    id: nanoid(),
    name,
    schemaVersion: SCHEMA_VERSION,
    createdAt: now,
    updatedAt: now,
    settings: structuredClone(DEFAULT_SETTINGS),
    plot: {
      boundary,
      setbacks: { front: 5, rear: 3, left: 3, right: 3 },
    },
    levels: [
      {
        id: groundId,
        name: 'Ground Floor',
        baseElevation: 0,
        height: DEFAULT_STANDARDS.floorHeight,
        footprint: rect(3, 3, plotWidth - 6, plotDepth - 8), // buildable region inside setbacks
        spaces: [],
        props: [],
      },
    ],
    stairs: [],
  }
}

/** Sensible floor name for the nth level (0 = ground). Shared by add-floor and duplicate-floor. */
export function ordinalFloorName(n: number): string {
  if (n === 0) return 'Ground Floor'
  const ordinals = ['1st', '2nd', '3rd']
  const o = ordinals[n - 1] ?? `${n}th`
  return `${o} Floor`
}

function rect(x: number, y: number, w: number, h: number) {
  return [
    { x, y },
    { x: x + w, y },
    { x: x + w, y: y + h },
    { x, y: y + h },
  ]
}

/** Wrapper so this module has a single place that touches the clock. */
function nowMs(): number {
  return Date.now()
}
